"use client";

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { isEmpty } from "lodash";

import { BffComponentType } from "~/bff/ComponentsConfig";
import { FilterOptionChipsNames } from "~/bff/types/FilterOptionChipsNames";
import { AccessibilityKeys } from "~/constants/accessibility";
import { shoppingTypeFilterComponentConfig } from "~/constants/components-configs/shop-type-filter-component-config";
import { AccessibilityLabelsContext } from "~/context/accessibility-label/context";
import { splitFilters } from "~/helpers/get-filter-values-for-request";
import { replaceCelsiusDegreeSymbols } from "~/helpers/replace-celsius-degree-symbols/index";
import { replaceCmsVariables } from "~/helpers/replace-cms-variables";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { CloseTiny24 } from "~/theme/icons/components/close-tiny";

import { AssertiveA11yComponent } from "../assertive-a11y-component/component";
import { ProductsList } from "../products-list/component";
import { UniversalComponent } from "../universal-component/component";

import { Chip } from "./components/chip/component";
import { ProductsFilters } from "./components/products-filters/component";
import { DEFAULT_PRODUCT_TYPE } from "./components/products-filters/components/filter/components/shop-filter/helpers/shop-filter.const";
import { DEFAULT_PRODUCTS_FILTER_PANEL_TEXT } from "./components/products-filters/constants";
import { ProductsTopBar } from "./components/products-top-bar/component";
import {
  DEFAULT_PAGE_SIZE,
  PRODUCTS_LAYOUT_CLASSES,
  ProductsLayoutProps,
  SHOP_FILTER_DROPDOWN,
  SHOP_FILTER_KEY,
} from "./constants";
import { MobileShopFilterContainer, ProductsLayoutContainer } from "./styled";

export const ProductsLayout: React.FC<ProductsLayoutProps> = ({
  totalCount,
  onLoadMore,
  onLoadPrevious,
  setActiveSortingParam,
  activeSortingParam,
  sortingParams,
  isLoading,
  productsCaption,
  products,
  filtersData,
  appliedFilters,
  onFiltersClick,
  onAllFiltersClear,
  onChipClick,
  productFilterPanelText,
  productSortSelectorText,
  progressBarText,
  loadMoreButtonText,
  loadPreviousButtonText,
  showPreviousButton,
  productFilterClearAllButtonText,
  colorPalette,
  onShopFilterClick,
  isShopFilter,
  currentStore,
  onUpdateStore,
  breadcrumbs,
  onShopFilterDropdown,
  setShopFilterSortingParam,
  shopFilter,
  shopFilterSortingParam,
  productToPageMap,
  pageNumber,
  restorationRef,
  shoppingTypeDropdown,
  onShopFilterChipClick,
  widgetData,
  sortOrderForGA,
}) => {
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [isShopFilterChipOptionShow, setIsShopFilterChipOptionShow] =
    useState<boolean>(false);
  const [isShopFilterChipToClose, setIsShopFilterChipToClose] =
    useState<boolean>(false);
  const [filterChipLabel, setFilterChipLabel] = useState<React.ReactNode>("");
  const [isFilterChipAllClear, setIsFilterChipAllClear] = useState<boolean>(false);

  const isShopFilterChipOption = useMemo(() => {
    return shopFilterSortingParam === DEFAULT_PRODUCT_TYPE ||
      shopFilterSortingParam === ""
      ? false
      : true;
  }, [shopFilterSortingParam]);

  const shopingTypeFilterChipName = useMemo(() => {
    return shopFilter?.props?.filterOptionChipsNames?.[
      shopFilterSortingParam as keyof FilterOptionChipsNames
    ];
  }, [shopFilter?.props?.filterOptionChipsNames, shopFilterSortingParam]);

  useEffect(() => {
    setIsShopFilterChipOptionShow(isShopFilterChipOption);
    if (isShopFilterChipOption) {
      setIsShopFilterChipToClose(false);
    }
  }, [isShopFilterChipOption, isShopFilterChipOptionShow]);

  const chipsForRender = useMemo(() => {
    const filters = { ...appliedFilters };
    if (isShopFilter && currentStore) {
      const storeKey = currentStore?.geomodifier
        ? currentStore?.geomodifier
        : currentStore?.address?.city;
      filters[`${SHOP_FILTER_KEY}*${storeKey}`] = 1;

      if (isShopFilterChipOptionShow) {
        filters[`${SHOP_FILTER_DROPDOWN}*${shopingTypeFilterChipName}`] = 1;
      }
    }

    return Object.keys(filters)
      .filter((key) => filters[key])
      .map((key) => {
        const label = splitFilters(key)?.[1];
        return { key, label: replaceCelsiusDegreeSymbols(label) };
      });
  }, [
    appliedFilters,
    isShopFilter,
    currentStore,
    isShopFilterChipOptionShow,
    shopingTypeFilterChipName,
  ]);

  const filtersCaptionMobile = useMemo(() => {
    return `${productFilterPanelText ?? DEFAULT_PRODUCTS_FILTER_PANEL_TEXT} ${
      chipsForRender.length ? `(${chipsForRender.length})` : ""
    }`;
  }, [chipsForRender.length, productFilterPanelText]);

  const handleFiltersOpen = useCallback(() => {
    setFiltersOpen(true);
  }, []);

  const handleFiltersClose = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  const handleChipClick = (filterName?: string) => {
    if (filterName?.includes(SHOP_FILTER_DROPDOWN)) {
      onShopFilterChipClick();
      setIsShopFilterChipOptionShow(false);
      setIsShopFilterChipToClose(true);
    } else if (filterName?.includes(SHOP_FILTER_KEY) && currentStore) {
      onShopFilterClick(currentStore);
    } else {
      filterName && onChipClick?.(filterName);
    }
  };

  const shopFilterProps = useMemo(() => {
    return {
      ...shopFilter?.props,
      onClick: onShopFilterClick,
      onFiltersClick: onFiltersClick,
      isShopFilter: isShopFilter,
      currentStore: currentStore,
      onUpdateStore: onUpdateStore,
      shopFilterSortingParam: shopFilterSortingParam,
      setShopFilterSortingParam: setShopFilterSortingParam,
      onShopFilterDropdown: onShopFilterDropdown,
      setIsShopFilterChipOptionShow: setIsShopFilterChipOptionShow,
      isShopFilterChipToClose: isShopFilterChipToClose,
      shoppingTypeDropdown,
      breadcrumbs,
      numFound: totalCount,
      sortOrderForGA,
    };
  }, [
    shopFilter?.props,
    onShopFilterClick,
    onFiltersClick,
    isShopFilter,
    currentStore,
    onUpdateStore,
    shopFilterSortingParam,
    setShopFilterSortingParam,
    onShopFilterDropdown,
    isShopFilterChipToClose,
    shoppingTypeDropdown,
    breadcrumbs,
    totalCount,
    sortOrderForGA,
  ]);

  const accessibilityLabelsContext = useContext(AccessibilityLabelsContext);

  const a11yFilterChipIsRemovedText = replaceCmsVariables(
    accessibilityLabelsContext?.[AccessibilityKeys.FilterChipRemoved],
    {
      chip_name: filterChipLabel as string,
    },
  );

  const handleFilterChipA11yLabel = useCallback((label: React.ReactNode) => {
    setIsFilterChipAllClear(false);
    setFilterChipLabel(label);
  }, []);

  const onClearAllClick = useCallback(() => {
    onAllFiltersClear?.();
    setIsFilterChipAllClear(true);
  }, [onAllFiltersClear]);

  const announcementText = isFilterChipAllClear
    ? accessibilityLabelsContext?.[AccessibilityKeys.FilterChipClearAll]
    : filterChipLabel
      ? a11yFilterChipIsRemovedText
      : "";

  return (
    <>
      <MobileShopFilterContainer>
        <UniversalComponent
          props={{
            ...shopFilterProps,
            className: PRODUCTS_LAYOUT_CLASSES.mobileShopFilter,
          }}
          component={shopFilter?.component as BffComponentType}
          componentsConfigs={[shoppingTypeFilterComponentConfig]}
        />
      </MobileShopFilterContainer>
      <ProductsLayoutContainer
        isFiltersOpen={isFiltersOpen}
        isChips={!!chipsForRender.length}
        id="products-layout"
      >
        {!isEmpty(filtersData) && (
          <ProductsFilters
            appliedFilters={appliedFilters}
            numberItems={totalCount}
            onFiltersClick={onFiltersClick}
            filtersData={filtersData}
            isFiltersOpen={isFiltersOpen}
            onFiltersClose={handleFiltersClose}
            onAllFiltersClear={onAllFiltersClear}
            filtersCaptionDesktop={productFilterPanelText || ""}
            filtersCaptionMobile={filtersCaptionMobile}
            colorPalette={colorPalette}
            onShopFilterClick={onShopFilterClick}
            isShopFilter={isShopFilter}
            currentStore={currentStore}
            onUpdateStore={onUpdateStore}
            productFilterClearAllButtonText={productFilterClearAllButtonText}
            shopFilter={shopFilter}
            setShopFilterSortingParam={setShopFilterSortingParam}
            onShopFilterDropdown={onShopFilterDropdown}
            setIsShopFilterChipOptionShow={setIsShopFilterChipOptionShow}
            shopFilterProps={shopFilterProps}
          />
        )}
        <Box
          id="product-list"
          className={PRODUCTS_LAYOUT_CLASSES.productListWrapper}
        >
          <Box>
            <ProductsTopBar
              className={PRODUCTS_LAYOUT_CLASSES.topBar}
              caption={productsCaption}
              captionMobile={filtersCaptionMobile}
              productSortSelectorText={productSortSelectorText}
              sortingParams={sortingParams}
              activeSortingParam={activeSortingParam}
              setActiveSortingParam={setActiveSortingParam}
              onFiltersOpen={handleFiltersOpen}
            />
            <Box className={PRODUCTS_LAYOUT_CLASSES.filterChipsContainer}>
              {chipsForRender.map(({ key, label }) => (
                <Chip
                  key={key}
                  label={label}
                  value={key}
                  onDelete={handleChipClick}
                  className={PRODUCTS_LAYOUT_CLASSES.filterChip}
                  handleFilterChipA11yLabel={handleFilterChipA11yLabel}
                  deleteIcon={<CloseTiny24 title="clear" />}
                />
              ))}
              {!!chipsForRender.length && (
                <Button
                  variant="text"
                  className="body2 underlined"
                  onClick={onClearAllClick}
                  {...getTestAutomationProps("clear-all")}
                >
                  {productFilterClearAllButtonText}
                </Button>
              )}
              <AssertiveA11yComponent announcementText={announcementText ?? ""} />
            </Box>
          </Box>
          <ProductsList
            onLoadMore={onLoadMore}
            onLoadPrevious={onLoadPrevious}
            loadMoreButtonText={loadMoreButtonText}
            loadPreviousButtonText={loadPreviousButtonText}
            showPreviousButton={showPreviousButton}
            progressBarText={progressBarText}
            products={products}
            totalCount={totalCount}
            pageSize={DEFAULT_PAGE_SIZE}
            isLoading={isLoading}
            breadcrumbs={breadcrumbs}
            productToPageMap={productToPageMap}
            pageNumber={pageNumber}
            restorationRef={restorationRef}
            widgetData={widgetData}
          />
        </Box>
      </ProductsLayoutContainer>
    </>
  );
};
